import { useRef, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Camera,
  X,
  Trash2,
  Sparkles,
  Loader2,
  ImagePlus,
  Check,
} from "lucide-react";

/**
 * Scanner de produto — o lojista tira fotos do produto (e/ou do código),
 * a IA extrai os dados e preenche um rascunho para confirmação.
 * Ele ajusta preço/estoque e salva (POST /api/products — com dedup).
 */
export default function ProductScanner({ open, onClose, onCreated }) {
  const [images, setImages] = useState([]); // base64 strings (no prefix)
  const [analyzing, setAnalyzing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);
  const [selectedImageIdx, setSelectedImageIdx] = useState(0); // qual foto usar como imagem do produto
  const fileRef = useRef(null);

  if (!open) return null;

  const addFiles = async (files) => {
    const arr = Array.from(files || []).slice(0, 3 - images.length);
    const b64s = await Promise.all(
      arr.map(
        (f) =>
          new Promise((res, rej) => {
            const r = new FileReader();
            r.onload = () => {
              const s = String(r.result);
              // strip "data:image/xxx;base64,"
              const idx = s.indexOf(",");
              res(idx >= 0 ? s.slice(idx + 1) : s);
            };
            r.onerror = rej;
            r.readAsDataURL(f);
          })
      )
    );
    setImages((prev) => [...prev, ...b64s]);
  };

  const analyze = async () => {
    if (images.length === 0) {
      toast.error("Adicione pelo menos 1 foto");
      return;
    }
    setAnalyzing(true);
    try {
      const { data } = await api.post("/products/scan", {
        images_base64: images,
      });
      setDraft({
        name: data.name || "",
        sku: data.sku || "",
        category: data.category || "",
        description: data.description || "",
        cost_price: Number(data.suggested_cost) || 0,
        sale_price: Number(data.suggested_price) || 0,
        stock: 1,
        min_stock: 5,
        is_public: true,
        image_url: "",
        _confidence: data.confidence,
        _brand: data.brand,
        _color: data.color,
      });
      toast.success("Produto analisado! Confirme e salve.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao analisar fotos");
    } finally {
      setAnalyzing(false);
    }
  };

  const generateImage = async () => {
    // deprecated — now handled by save() flow
  };

  const save = async (mode = "none") => {
    // mode: "my_photo" | "ai_image" | "none"
    if (!draft?.name) {
      toast.error("Preencha o nome do produto");
      return;
    }
    setSaving(true);
    try {
      let imageUrl = "";

      // Upload a foto tirada pelo usuário (se escolhida)
      if (mode === "my_photo" && images[selectedImageIdx]) {
        try {
          const up = await api.post("/products/upload-image", {
            image_base64: images[selectedImageIdx],
            filename_hint: draft.name,
          });
          imageUrl = up.data?.image_url || "";
        } catch (e) {
          toast.error("Erro ao enviar foto — salvando sem ela");
        }
      }

      const payload = { ...draft, image_url: imageUrl };
      delete payload._confidence;
      delete payload._brand;
      delete payload._color;
      const { data } = await api.post("/products", payload);
      if (data._merged) {
        toast.success(
          `Já existia! +${data._added_stock} un. no estoque (total ${data.stock}).`
        );
      } else {
        toast.success("Produto criado ✓");
      }

      // Gera foto com IA depois de criar
      if (mode === "ai_image" && !data.image_url) {
        toast.info("Gerando foto com IA… pode demorar uns 10s");
        try {
          await api.post("/ai/chat", {
            message: `Gera uma imagem profissional para o produto "${draft.name}" e adiciona nele. Use set_product_image_ai.`,
          });
          toast.success("Foto gerada e associada ao produto 📸");
        } catch {}
      }

      setImages([]);
      setDraft(null);
      setSelectedImageIdx(0);
      onCreated && onCreated(data.id);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div
        className="bg-white rounded-sm max-w-2xl w-full max-h-[92vh] overflow-y-auto"
        data-testid="scanner-modal"
      >
        <div className="sticky top-0 bg-white border-b border-slate-200 p-4 flex justify-between items-center z-10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#2563EB] font-semibold">
              Scanner IA
            </div>
            <h2 className="font-display text-xl font-bold">
              Cadastrar por foto
            </h2>
          </div>
          <button onClick={onClose} data-testid="scanner-close">
            <X size={20} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {!draft && (
            <>
              <p className="text-sm text-slate-600">
                Tire 1 a 3 fotos: do produto e, se houver, do código de barras
                ou etiqueta. A IA identifica e cria o rascunho.
              </p>

              {/* Image grid */}
              <div className="grid grid-cols-3 gap-2">
                {images.map((b64, i) => (
                  <div
                    key={i}
                    className="relative border-2 border-slate-200 rounded-sm overflow-hidden aspect-square"
                  >
                    <img
                      src={`data:image/jpeg;base64,${b64}`}
                      alt=""
                      className="w-full h-full object-cover"
                    />
                    <button
                      onClick={() =>
                        setImages((prev) => prev.filter((_, j) => j !== i))
                      }
                      className="absolute top-1 right-1 bg-black/70 text-white p-1 rounded-sm"
                    >
                      <Trash2 size={12} />
                    </button>
                  </div>
                ))}
                {images.length < 3 && (
                  <>
                    <button
                      onClick={() => fileRef.current?.click()}
                      data-testid="scanner-add-photo"
                      className="border-2 border-dashed border-slate-300 hover:border-[#2563EB] hover:text-[#2563EB] rounded-sm aspect-square flex flex-col items-center justify-center text-slate-500 text-xs gap-1"
                    >
                      <Camera size={22} />
                      Tirar foto
                    </button>
                    <input
                      ref={fileRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      multiple
                      className="hidden"
                      onChange={(e) => addFiles(e.target.files)}
                    />
                  </>
                )}
              </div>

              <button
                onClick={analyze}
                disabled={analyzing || images.length === 0}
                data-testid="scanner-analyze"
                className="w-full bg-gradient-to-r from-[#2563EB] to-[#0A1628] hover:opacity-90 disabled:opacity-40 text-white py-3 rounded-sm text-sm font-semibold flex items-center justify-center gap-2"
              >
                {analyzing ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Analisando
                    com IA…
                  </>
                ) : (
                  <>
                    <Sparkles size={16} /> Analisar fotos
                  </>
                )}
              </button>
            </>
          )}

          {draft && (
            <>
              <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs p-2.5 rounded-sm flex items-center gap-2">
                <Check size={14} />
                <span>
                  Produto identificado ({draft._confidence}) — revise e ajuste
                  preço/estoque antes de salvar.
                </span>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs uppercase tracking-wider text-slate-600 mb-1">
                    Nome *
                  </label>
                  <input
                    type="text"
                    value={draft.name}
                    onChange={(e) =>
                      setDraft({ ...draft, name: e.target.value })
                    }
                    data-testid="scanner-name"
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-slate-600 mb-1">
                    SKU / Código de barras
                  </label>
                  <input
                    type="text"
                    value={draft.sku}
                    onChange={(e) =>
                      setDraft({ ...draft, sku: e.target.value })
                    }
                    data-testid="scanner-sku"
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-slate-600 mb-1">
                    Categoria
                  </label>
                  <input
                    type="text"
                    value={draft.category}
                    onChange={(e) =>
                      setDraft({ ...draft, category: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-slate-600 mb-1">
                    Custo (R$)
                  </label>
                  <input
                    type="number"
                    value={draft.cost_price}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        cost_price: Number(e.target.value),
                      })
                    }
                    data-testid="scanner-cost"
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-slate-600 mb-1">
                    Venda (R$) *
                  </label>
                  <input
                    type="number"
                    value={draft.sale_price}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        sale_price: Number(e.target.value),
                      })
                    }
                    data-testid="scanner-price"
                    className="w-full px-3 py-2 border border-[#2563EB] rounded-sm text-sm font-semibold"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-slate-600 mb-1">
                    Quantidade
                  </label>
                  <input
                    type="number"
                    value={draft.stock}
                    onChange={(e) =>
                      setDraft({ ...draft, stock: Number(e.target.value) })
                    }
                    data-testid="scanner-stock"
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-wider text-slate-600 mb-1">
                    Estoque mínimo
                  </label>
                  <input
                    type="number"
                    value={draft.min_stock}
                    onChange={(e) =>
                      setDraft({
                        ...draft,
                        min_stock: Number(e.target.value),
                      })
                    }
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs uppercase tracking-wider text-slate-600 mb-1">
                    Descrição
                  </label>
                  <textarea
                    rows={2}
                    value={draft.description}
                    onChange={(e) =>
                      setDraft({ ...draft, description: e.target.value })
                    }
                    className="w-full px-3 py-2 border border-slate-300 rounded-sm text-sm"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-2 pt-2">
                {images.length > 0 && (
                  <div className="border border-slate-200 rounded-sm p-3 bg-slate-50">
                    <div className="text-[10px] uppercase tracking-wider text-slate-600 mb-2 font-semibold">
                      Escolher foto do produto
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      {images.map((b64, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setSelectedImageIdx(i)}
                          data-testid={`scanner-pick-photo-${i}`}
                          className={`relative rounded-sm overflow-hidden aspect-square border-2 transition-all ${
                            selectedImageIdx === i
                              ? "border-[#2563EB] ring-2 ring-[#2563EB]/30"
                              : "border-slate-200 hover:border-slate-400"
                          }`}
                        >
                          <img
                            src={`data:image/jpeg;base64,${b64}`}
                            alt={`Foto ${i + 1}`}
                            className="w-full h-full object-cover"
                          />
                          {selectedImageIdx === i && (
                            <div className="absolute top-1 right-1 bg-[#2563EB] text-white rounded-full w-5 h-5 flex items-center justify-center">
                              <Check size={12} />
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-1.5">
                      A foto selecionada será usada se você clicar em "Usar
                      minha foto". A IA pode gerar uma versão profissional como
                      alternativa.
                    </div>
                  </div>
                )}

                <button
                  onClick={() => save("my_photo")}
                  disabled={saving || images.length === 0}
                  data-testid="scanner-save-my-photo"
                  className="bg-[#2563EB] hover:bg-[#1D4ED8] disabled:opacity-50 text-white py-2.5 rounded-sm text-sm font-semibold flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <ImagePlus size={14} />
                  )}
                  Salvar com minha foto
                </button>
                <button
                  onClick={() => save("ai_image")}
                  disabled={saving}
                  data-testid="scanner-save-with-image"
                  className="bg-gradient-to-r from-[#2563EB] to-[#0A1628] hover:opacity-90 disabled:opacity-50 text-white py-2.5 rounded-sm text-sm font-semibold flex items-center justify-center gap-2"
                >
                  {saving ? (
                    <Loader2 size={14} className="animate-spin" />
                  ) : (
                    <Sparkles size={14} />
                  )}
                  Salvar e gerar foto profissional (IA)
                </button>
                <button
                  onClick={() => save("none")}
                  disabled={saving}
                  data-testid="scanner-save"
                  className="border border-slate-300 hover:bg-slate-50 py-2.5 rounded-sm text-sm font-semibold"
                >
                  Salvar sem foto
                </button>
                <button
                  onClick={() => setDraft(null)}
                  className="text-xs text-slate-500 underline"
                >
                  ← voltar e tirar outras fotos
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
